import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';

export interface MultiSelectOption<T extends string> {
  value: T;
  label: string;
  /** Optional row count shown next to the option (e.g., "Research (12)"). */
  count?: number;
}

export interface MultiSelectDropdownProps<T extends string> {
  label: string;
  options: MultiSelectOption<T>[];
  values: T[];
  onChange: (next: T[]) => void;
  /** Label variant when no options are selected. Defaults to "All". */
  allLabel?: string;
}

/**
 * Generic multi-select dropdown for list-page filtering per entity-management
 * guidelines §1.3 (search + scoped dropdowns replace hierarchical navigation).
 *
 * Click the button to open; click an option to toggle; click outside to close.
 * The button shows a count badge when any option is selected.
 *
 * Reused by Templates, Tools, Workforce list pages.
 */
export function MultiSelectDropdown<T extends string>({
  label,
  options,
  values,
  onChange,
  allLabel = 'All',
}: MultiSelectDropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selected = new Set(values);
  const toggle = (val: T) => {
    const next = new Set(selected);
    if (next.has(val)) next.delete(val);
    else next.add(val);
    onChange(Array.from(next) as T[]);
  };

  const buttonText = selected.size === 0 ? allLabel : `${selected.size} selected`;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 px-3 py-2 border rounded-lg bg-surface text-sm hover:bg-surface-hover transition"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="text-muted">{label}:</span>
        <span className="text-heading font-medium">{buttonText}</span>
        <ChevronDown size={14} className={`text-faint transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute right-0 top-full mt-1 min-w-[200px] bg-surface border rounded-lg shadow-lg z-20 py-1 max-h-80 overflow-auto"
        >
          {selected.size > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="w-full px-3 py-1.5 text-left text-xs text-muted hover:bg-surface-hover border-b"
            >
              Clear all
            </button>
          )}
          {options.map((opt) => {
            const on = selected.has(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={on}
                onClick={() => toggle(opt.value)}
                className="w-full flex items-center justify-between px-3 py-2 text-sm text-body hover:bg-surface-hover text-left"
              >
                <span className="flex items-center gap-2">
                  <span
                    className={`w-4 h-4 rounded border flex items-center justify-center ${
                      on ? 'bg-accent border-accent' : 'border-strong'
                    }`}
                  >
                    {on && <Check size={12} className="text-white" />}
                  </span>
                  <span className={on ? 'text-heading font-medium' : ''}>{opt.label}</span>
                </span>
                {opt.count != null && <span className="text-xs text-faint">{opt.count}</span>}
              </button>
            );
          })}
          {options.length === 0 && <div className="px-3 py-2 text-xs text-faint">No options</div>}
        </div>
      )}
    </div>
  );
}
